import React, { useState, useEffect } from 'react';
import { Phone, Mail, Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      {/* Top bar */}
      <div className="bg-slate-800 text-white py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:0160043123" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <Phone size={16} />
              <span className="hidden sm:inline">01 60 04 31 23</span>
            </a>
            <a href="mailto:esblyauto@gmail.com" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <Mail size={16} />
              <span className="hidden sm:inline">esblyauto@gmail.com</span>
            </a>
          </div>
          <div className="text-xs sm:text-sm">
            {t.topBar.hours}
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_gearbox-shop/artifacts/cj8moyc7_1000159555.jpg"
              alt="ESBLY AUTO"
              className="h-16 sm:h-20 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 leading-tight">
                ESBLY AUTO
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium"
            >
              {t.nav.home}
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium"
            >
              {t.nav.services}
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium"
            >
              {t.nav.whyChooseUs}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium"
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Toggle & CTA */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-slate-700 hover:text-red-600 transition-colors"
              title={language === 'en' ? 'Switch to French' : 'Passer en anglais'}
            >
              <Globe size={20} />
              <span className="font-medium">{language === 'en' ? 'FR' : 'EN'}</span>
            </button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              {t.nav.getQuote}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium text-left py-2"
            >
              {t.nav.home}
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium text-left py-2"
            >
              {t.nav.services}
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium text-left py-2"
            >
              {t.nav.whyChooseUs}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-slate-700 hover:text-red-600 transition-colors font-medium text-left py-2"
            >
              {t.nav.contact}
            </button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-slate-700 hover:text-red-600 transition-colors font-medium text-left py-2"
            >
              <Globe size={20} />
              <span>{language === 'en' ? 'Français' : 'English'}</span>
            </button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-red-600 hover:bg-red-700 text-white w-full transition-colors"
            >
              {t.nav.getQuote}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
